import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { CarConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import * as React from "react";
import { setCookie } from "components/utility/cookie";
import { CarLastSearchData, LastSearchData } from "stores/wizard/LastSearchData";
import { Travelers, Room } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";

export class CarWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  public lastSearchData?: CarLastSearchData;

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return (this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB) && this.isDesktop;
  }

  @observable
  public config: CarConfig;

  @observable
  public preferredBrand: string = "";

  @observable
  public pickupTime: string;

  private pickupTimeCannonicalMinutesDefault = 630;
  public pickupTimeCannonicalMinutes: number = this.pickupTimeCannonicalMinutesDefault; //"1030AM"

  @observable
  public returnTime: string;

  @action
  public setDefaultShowingTime = () => {
    this.pickupTime = this.returnTime = this.config.defaultShowingTime;
  };

  public subLOBState: string = "";

  @observable
  public carCouponCodeType: string = "noCode";

  @observable
  public couponCode0: string;

  @observable
  public couponCode1: string;

  @observable
  public couponCode2: string;

  @computed
  public get date() {
    return this.globalState.date;
  }

  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = subLOBState;
  }

  // Ground Transportation
  @observable
  public roundTrip: boolean = false;

  @observable
  public tabSelected: boolean = true;

  @observable
  public nonHotelTravelersInvalidKey: string | "";

  @observable
  public hotelTravelersInvalidKey: string | "";

  public toggleBookRoundtrip() {
    this.roundTrip = !this.roundTrip;
  }

  public toggleTabSelected() {
    this.tabSelected = !this.tabSelected;
  }

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers);
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateHotelTravelersSelection(travelers);
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public validateLessThanNTravelers = () => {
    if (this.subLOBState === "fromAirport") {
      if (!this.globalState.validateLessThanNTravelers(this.hotelTravelersMetadata, this.config.travelers)) {
        this.hotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

        return false;
      }
    } else if (this.subLOBState === "toAirport") {
      if (!this.globalState.validateLessThanNTravelers(this.nonHotelTravelersMetadata, this.config.travelers)) {
        this.nonHotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

        return false;
      }
    } else {
      if (!this.globalState.validateLessThanNTravelers(this.hotelTravelersMetadata, this.config.travelers)) {
        this.hotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

        return false;
      }
    }

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers();

  @computed
  public get travelers() {
    const nonHotelRoom: [Room] = [
      {
        adults: 1,
        children: [],
        infants: [],
      },
    ];

    const applicableSubLOB = this.subLOBState ? this.subLOBState : "fromAirport";
    const applicableTravelerState =
      applicableSubLOB === "fromAirport" ? this.globalState.travelers.hotel : this.globalState.travelers.nonHotel;
    const applicableRoomsForTravelers: Room[] = applicableTravelerState.rooms.map((room: Room) => {
      const applicableAdultsPerRoom =
        room.adults > this.config.travelers.maxAdultsPerRoom ? this.config.travelers.maxAdultsPerRoom : room.adults;
      const combinedChildren = room.children.concat(room.infants);
      const applicableChildrenPerRoom =
        combinedChildren.length > this.config.travelers.maxChildrenPerRoom
          ? combinedChildren.splice(0, this.config.travelers.maxChildrenPerRoom)
          : combinedChildren;

      return {
        adults: applicableAdultsPerRoom,
        children: applicableChildrenPerRoom,
        infants: [],
      };
    });

    nonHotelRoom[0] = applicableRoomsForTravelers[0];

    const gtTravelers = {
      hotel: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotel.infantSeating,
      },
      hotelPackage: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotelPackage.infantSeating,
      },
      nonHotel: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
    };

    return gtTravelers;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  //validations
  @observable
  public originInvalidKey: string | "";

  @observable
  public destinationInvalidKey: string | "";

  @action
  public validateOriginField = () => {
    if (!this.globalState.validateOriginField()) {
      this.originInvalidKey = this.config.location.origin.invalidLocationMessageKey;

      return false;
    }

    this.originInvalidKey = "";

    return true;
  };

  @action
  public validateDestinationFiled = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;

      return false;
    }

    return true;
  };

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @observable
  public timeStartInvalidKey: string = "";

  @action
  public validateMaxDateRange = () => {
    const { start, end } = this.date;
    const { maxDaysRange: gtMaxDaysRange, invalidMaxDatesRangeMessageToken } = this.config.gtConfig.date;
    const { maxDaysRange: carMaxDaysRange } = this.config.date;

    if (!this.tabSelected && !this.globalState.validateMaxDateRange(start, end, gtMaxDaysRange)) {
      this.dateEndInvalidKey = invalidMaxDatesRangeMessageToken;

      return false;
    }

    if (!this.globalState.validateMaxDateRange(start, end, carMaxDaysRange)) {
      this.dateEndInvalidKey = invalidMaxDatesRangeMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateStartTime = () => {
    const isToday = (dt: Date) => {
      const today = new Date();

      return (
        dt.getDate() === today.getDate() &&
        dt.getMonth() === today.getMonth() &&
        dt.getFullYear() === today.getFullYear()
      );
    };

    const isSelectedHourValid = () => {
      const today = new Date();
      const now = today.getHours() * 60 + today.getMinutes();

      return this.pickupTimeCannonicalMinutes >= now;
    };

    if (isToday(this.date.start) && !isSelectedHourValid()) {
      this.timeStartInvalidKey = this.config.date.invalidStartTimeMessageToken!;

      return false;
    }

    return true;
  };

  @action
  public validateStartTimeField = () => this.validateStartTime();

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public resetValidations = () => {
    this.originInvalidKey = "";
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.timeStartInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  public validateForm = () => {
    const validations: boolean[] = [
      this.validateOriginField(),
      this.validateDestinationFiled(),
      this.validateEndDateField(),
      this.validateStartTimeField(),
      this.validateTravelersField(),
    ];

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.timeStartInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  @action
  public updateCouponCodeType = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    const carCouponCodeType = selectEvent.target.value;
    this.carCouponCodeType = carCouponCodeType;
    this.couponCode0 = "";
    this.couponCode1 = "";
    this.couponCode2 = "";
  };

  @action
  public updateCouponCode = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value;
    if (index === 0) {
      this.couponCode0 = code;
    } else if (index === 1) {
      this.couponCode1 = code;
    } else if (index === 2) {
      this.couponCode2 = code;
    }
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateOriginSelection(selection);
    if (!this.tabSelected) {
      this.originInvalidKey = "";

      return;
    }
    if (!this.globalState.location.destination.value) {
      this.globalState.updateDestinationSelection(selection);
    }
    this.originInvalidKey = "";
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.globalState.updateDestinationFromCookie(value, regionID, countryCode, latLong, ttla);
  }

  @action
  public updateOriginFromCookie(value: string, regionID: string, countryCode: string, latLong: string, ttla: string) {
    this.globalState.updateOriginFromCookie(value, regionID, countryCode, latLong, ttla);
    this.originInvalidKey = "";
  }

  @action
  public setPreferredBrand = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.preferredBrand = selectEvent.target.value;
    this.couponCode0 = "";
    this.couponCode1 = "";
    this.couponCode2 = "";
  };

  @action
  public setPickupTime = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.pickupTime = selectEvent.target.value;
    const cannonicalMinutes =
      selectEvent.target && selectEvent.target.selectedOptions
        ? selectEvent.target.selectedOptions[0].dataset["time"]
        : "";
    this.pickupTimeCannonicalMinutes = cannonicalMinutes
      ? parseInt(cannonicalMinutes, 10)
      : this.pickupTimeCannonicalMinutesDefault;
    this.timeStartInvalidKey = "";
  };

  @action
  public setReturnTime = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.returnTime = selectEvent.target.value;
  };

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const origin = this.location.origin;
      const originMetaData = this.location.origin.metaData;
      const destination = this.location.destination;
      const detinationMetaData = this.location.destination.metaData;
      const date = this.date;
      const couponCode0 = this.couponCode0;
      const couponCode1 = this.couponCode1;
      const couponCode2 = this.couponCode2;
      this.setDefaultShowingTime();

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        car: {
          origin_value: origin.value ? encodeURIComponent(origin.value) : "",
          origin_regionID: originMetaData.regionId ? encodeURIComponent(originMetaData.regionId) : "",
          origin_ttla: originMetaData.ttla ? encodeURIComponent(originMetaData.ttla) : "",
          origin_countryCode: originMetaData.countryCode ? encodeURIComponent(originMetaData.countryCode) : "",
          origin_latLong: originMetaData.latLong ? encodeURIComponent(originMetaData.latLong) : "",
          destination_value: destination.value ? encodeURIComponent(destination.value) : "",
          destination_regionID: detinationMetaData.regionId ? encodeURIComponent(detinationMetaData.regionId) : "",
          destination_ttla: detinationMetaData.ttla ? encodeURIComponent(detinationMetaData.ttla) : "",
          destination_countryCode: detinationMetaData.countryCode
            ? encodeURIComponent(detinationMetaData.countryCode)
            : "",
          destination_latLong: detinationMetaData.latLong ? encodeURIComponent(detinationMetaData.latLong) : "",
          pickup_date: date.start ? date.start.toString() : "",
          dropoff_date: date.end ? date.end.toString() : "",
          pickup_time: this.pickupTime,
          return_time: this.returnTime,
          preferred_brand: this.preferredBrand,
          coupon_code_type: this.carCouponCodeType,
          coupon_code_0: couponCode0 ? encodeURIComponent(couponCode0) : "",
          coupon_code_1: couponCode1 ? encodeURIComponent(couponCode1) : "",
          coupon_code_2: couponCode2 ? encodeURIComponent(couponCode2) : "",
        },
      };
      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
      });
    }
  };

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;
    this.setDefaultShowingTime();

    if (this.canTrack && this.useLastSearch && lastSearchData) {
      const originValue = decodeURIComponent(lastSearchData.origin_value);
      const destinationValue = decodeURIComponent(lastSearchData.destination_value);
      const startDateFromCookie = new Date(lastSearchData.pickup_date);
      const endDateFromCookie = new Date(lastSearchData.dropoff_date);
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      this.updateOriginFromCookie(
        originValue !== "undefined" ? originValue : "",
        lastSearchData.origin_regionID ? lastSearchData.origin_regionID : "",
        lastSearchData.origin_countryCode ? lastSearchData.origin_countryCode : "",
        lastSearchData.origin_latLong ? lastSearchData.origin_latLong : "",
        lastSearchData.origin_ttla ? lastSearchData.origin_ttla : ""
      );

      if (originValue !== "undefined" && destinationValue === "undefined") {
        this.updateDestinationFromCookie(
          originValue,
          (lastSearchData.destination_regionID = lastSearchData.origin_regionID),
          (lastSearchData.destination_countryCode = lastSearchData.origin_countryCode),
          (lastSearchData.destination_latLong = lastSearchData.origin_latLong),
          (lastSearchData.destination_ttla = lastSearchData.origin_ttla)
        );
      } else {
        this.updateDestinationFromCookie(
          destinationValue !== "undefined" ? destinationValue : "",
          lastSearchData.destination_regionID ? lastSearchData.destination_regionID : "",
          lastSearchData.destination_countryCode ? lastSearchData.destination_countryCode : "",
          lastSearchData.destination_latLong ? lastSearchData.destination_latLong : "",
          lastSearchData.destination_ttla ? lastSearchData.destination_ttla : ""
        );
      }
      this.updateDateSelection(startDateToUse, endDateToUse);

      //populate from last search or from default if unavailable
      this.pickupTime = lastSearchData.pickup_time ? lastSearchData.pickup_time : this.pickupTime;
      this.returnTime = lastSearchData.return_time ? lastSearchData.return_time : this.returnTime;
      this.preferredBrand = lastSearchData.preferred_brand ? lastSearchData.preferred_brand : this.preferredBrand;
      this.carCouponCodeType = lastSearchData.coupon_code_type
        ? lastSearchData.coupon_code_type
        : this.carCouponCodeType;
      this.couponCode0 = lastSearchData.coupon_code_0 ? lastSearchData.coupon_code_0 : this.couponCode0;
      this.couponCode1 = lastSearchData.coupon_code_1 ? lastSearchData.coupon_code_1 : this.couponCode1;
      this.couponCode2 = lastSearchData.coupon_code_2 ? lastSearchData.coupon_code_2 : this.couponCode2;
    }
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
    }
  };

  constructor(config: CarConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.car;
    }
  }
}
